/**
 * 경계 타입(`MomentQueryScope`)의 **값 규약**을 지키는 변환기.
 *
 * `types/moments/moment-ops.ts`가 `MomentSearchFieldKey` · `MomentDateInt` ·
 * `platform*account` 같은 규약을 **타입으로만** 정한다. 그 규약을 지키는 변환기는
 * **여기 한 벌만 둔다** — 호출부가 자기 사본을 갖게 두지 마라.
 *
 * ⚠️ **`src/types/`에는 못 둔다** — 배치 규약이 *"런타임 값은 넣지 않는다"*다.
 * 그래서 도메인이 확정된 순수 로직 자리인 `utils/moments/`에 둔다.
 *
 * ⚠️ 여기 모여 있다고 전부 하나로 합칠 수 있는 것은 아니다. 날짜 변환이 **둘**인 것이
 * 그 예다 — 모양이 비슷하다고 합치면 동작이 바뀐다. 아래 각 함수의 ⚠️를 볼 것.
 */

import type { MomentSearchFieldKey } from 'src/types/moments/moment-scope';

/**
 * 검색 대상 필드 키의 전체 목록. **리포에 이것 하나뿐이어야 한다.**
 *
 * 키를 늘릴 일이 생기면 여기만 고친다.
 */
export const POST_SEARCH_FIELD_KEYS: MomentSearchFieldKey[] = ['body', 'account', 'author'];

/**
 * 아무 값 → `MomentSearchFieldKey[]`. 배열이 아니면 `undefined`.
 *
 * ⚠️ **배열이 아닐 때 `[]`가 아니라 `undefined`인 것이 규약이다.** 경계 타입에서
 * `refineFields`가 optional이고, 빈 배열("아무 필드도 안 고름")과 미지정은 뜻이 다르다 —
 * 서버가 빈 배열을 받으면 본문(`body`)으로 떨어뜨린다(사이드바 "전체 해제"가 실제로 그렇다).
 */
export function toPostSearchFieldKeys(value: unknown): MomentSearchFieldKey[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: MomentSearchFieldKey[] = [];
  for (const item of value) {
    const matched = POST_SEARCH_FIELD_KEYS.find((name) => name === item);
    if (matched) out.push(matched);
  }
  return out;
}

/** `Date`·문자열·숫자 어느 쪽으로도 오는 조회 기간 경계 */
export type PostDateBound = Date | string | number | null | undefined;

/**
 * **이미 `YYYYMMDD` 모양인** 값에서 숫자만 뽑는다. 키워드·브랜드가 쓴다.
 *
 * ⚠️ 아래 `toLocalPostDateInt`와 **합치지 않는다.** 이쪽은 `'YYYY-MM-DD'`·`YYYYMMDD`처럼
 * 이미 날짜가 적힌 값을 받고, 그쪽은 `Date` 객체를 로컬 타임존으로 읽는다. 입력이 다르다.
 */
export function toPostDateInt(value: string | number | null | undefined): number {
  const digits = String(value ?? '').replace(/\D/g, '');
  return Number.parseInt(digits, 10) || 0;
}

/**
 * `Date` 객체를 **로컬 타임존 기준** `YYYYMMDD` 정수로. 검색 화면이 쓴다.
 *
 * ⚠️ `toISOString()`을 쓰면 안 된다 — UTC로 바뀌어 한국 시간 오전 9시 이전이 전날이 된다.
 * 그래서 `getFullYear`/`getMonth`/`getDate`로 읽는다(`buildSearchFilters`가 하던 그대로).
 */
export function toLocalPostDateInt(value: PostDateBound): number {
  const d = new Date(value ?? Date.now());
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return parseInt(`${year}${month}${day}`, 10);
}

/**
 * 계정 식별자 — `platform*account`.
 *
 * ⚠️ **구분자 `*`가 사는 유일한 곳이다.** 이 규약은 서버의 `parseAccountPairs`가 되읽으므로
 * 한쪽만 바뀌면 계정 필터가 조용히 아무것도 못 맞춘다.
 *
 * ⚠️ **정규화는 하지 않는다.** 소문자화·빈 값 제거는 화면마다 다르다 — 브랜드는 하고
 * 키워드는 안 한다. 여기서 해 버리면 키워드 화면의 결과가 바뀐다(§4-15).
 * 필요한 화면이 이 함수를 감싸서 자기 정규화를 얹는다.
 */
export function toAccountKey(platform: string, account: string): string {
  return `${platform}*${account}`;
}

/**
 * 스칼라로도 오는 값을 문자열 배열로.
 *
 * ⚠️ **서버 요청 리더의 `readStringList`와 합치지 않는다.** 그쪽은 신뢰 경계라 **배열만**
 * 받아야 한다 — 스칼라를 감싸 주면 검증이 느슨해진다. 이쪽은 화면 상태를 읽는 자리라
 * `filter.country`가 문자열 하나로 오는 것을 감당해야 한다.
 */
export function toPostStringList(value: unknown): string[] | undefined {
  if (value == null) return undefined;
  const list = Array.isArray(value) ? value : [value];
  return list.filter((item): item is string => typeof item === 'string');
}
